import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  EXAM_BLOCK_LABELS,
  EXAM_BLOCK_MODES,
  guideBlockModesForSubject,
  presentationBlockModesForSubject,
  GAME_MODE_CATALOG,
  generateExamFromOptions,
  generateExamWithAi,
  mapGenerateExamError,
  type ExamResourceType,
  type GameMode,
  createBlockId,
  defaultPayloadForMode,
  validateExam,
  programExamAccessCode,
  programScheduledExam,
  mapExamJoinError,
  saveHostSession,
  type EvaluationBlock,
  type EvaluationExam,
} from '@interactive-room';
import { ExamAiBuildDialog } from './components/ExamAiBuildDialog';
import { GenerationLoadingOverlay } from '../components/ui/GenerationLoadingOverlay';
import { ExamBlockAccordion } from './components/ExamBlockAccordion';
import type { ExamAiBuildOptions } from './constants/examAiParams';
import { getExtensionOption } from './constants/examAiParams';
import { SUBJECT_CATALOG } from './constants/subjectCatalog';
import { buildHostLaunchUrl } from './hostLaunch';
import { resolveCourseProfile } from './courseContext';
import { buildPrintWorksheetUrl } from './print/buildPrintWorksheetUrl';
import { buildGuideContextFromBlocks } from './constants/blockAiParams';
import { parseMaterialIntent, readDeliveryProfileFromSearch, type MaterialIntent, type MaterialIntentNavigationState } from './constants/materialIntent';
import { deliveryProfileFromIntent } from './constants/deliveryProfile';
import {
  linkedSessionRefFromContext,
  cleanLinkedSessionTitle,
  type LinkedSessionContext,
} from './types/linkedSessionContext';
import {
  resolveGuideDesignBadge,
  type GuideDesignMeta,
} from './lib/guideDesignBadge';

import {
  emptyEvaluationExam,
  saveEvaluationExam,
  getEvaluationExam,
} from './examStorage';

function examWithCourseContext(exam: EvaluationExam, courseId?: string): EvaluationExam {
  if (!courseId || exam.courseId) return exam;
  return { ...exam, courseId };
}

interface ExamBuilderProps {
  examId?: string;
  defaultSubjectTag?: string;
  defaultResourceType?: ExamResourceType;
  /** Modelo 2 — ruta imprimible vs clase proyectable. */
  materialIntent?: MaterialIntent;
  /** Curso desde la URL — contexto IA implícito. */
  courseId?: string;
  onSaved?: (exam: EvaluationExam) => void;
}

export function ExamBuilder({
  examId,
  defaultSubjectTag,
  defaultResourceType = 'exam',
  materialIntent = 'print',
  courseId,
  onSaved,
}: ExamBuilderProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const initial = examId ? getEvaluationExam(examId) : null;
  const urlProfile = readDeliveryProfileFromSearch(searchParams);
  const urlIntent = parseMaterialIntent(searchParams.get('intent'));
  const resolvedIntent: MaterialIntent =
    urlIntent ??
    (initial?.deliveryFormat === 'interactive' || materialIntent === 'interactive'
      ? 'interactive'
      : initial?.deliveryProfile === 'assessment' || materialIntent === 'exam'
        ? 'exam'
        : 'print');
  const deliveryProfile =
    initial?.deliveryProfile ??
    urlProfile ??
    deliveryProfileFromIntent(resolvedIntent);
  const isInteractiveClass =
    deliveryProfile === 'interactive_class' ||
    (resolvedIntent === 'interactive' && !((initial?.resourceType ?? defaultResourceType) === 'guide'));
  const [exam, setExam] = useState<EvaluationExam>(() => {
    if (initial) return initial;
    const base = emptyEvaluationExam(
      'teacher',
      defaultResourceType,
      resolvedIntent === 'interactive' ? 'interactive' : 'print',
      deliveryProfile,
    );
    return defaultSubjectTag
      ? { ...base, subjectTag: defaultSubjectTag, ...(courseId ? { courseId } : {}) }
      : courseId
        ? { ...base, courseId }
        : base;
  });
  const isGuide = (exam.resourceType ?? defaultResourceType) === 'guide';
  // Fallback de nivel/asignatura para el diálogo IA cuando no hay linkedSessionContext
  // (evita que quede fijo en el default 5basico de ExamAiBuildDialog).
  const courseProfile = resolveCourseProfile({ courseId: exam.courseId ?? courseId });
  const blockModes = isGuide
    ? guideBlockModesForSubject(exam.subjectTag)
    : isInteractiveClass
      ? presentationBlockModesForSubject(exam.subjectTag)
      : EXAM_BLOCK_MODES;
  const [error, setError] = useState<string | null>(null);
  const [savedMsg, setSavedMsg] = useState<string | null>(null);
  const [programMsg, setProgramMsg] = useState<string | null>(null);
  const [programError, setProgramError] = useState<string | null>(null);
  const [programming, setProgramming] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [guideDesign, setGuideDesign] = useState<GuideDesignMeta | null>(null);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [expandedBlockId, setExpandedBlockId] = useState<string | null>(null);
  const aiAutoOpenedRef = useRef(false);
  const [linkedSessionContext, setLinkedSessionContext] = useState<LinkedSessionContext | null>(null);
  const [wizardTeacherPrompt, setWizardTeacherPrompt] = useState<string | undefined>();

  useEffect(() => {
    const state = location.state as MaterialIntentNavigationState | {
      generatedNotice?: string;
      fromRecipeNativeBlock?: boolean;
    } | null;

    const searchFlowProfile = new URLSearchParams(location.search).get('flowProfile');
    const stateFlowProfile =
      state && 'flowProfile' in state && typeof state.flowProfile === 'string'
        ? state.flowProfile
        : null;
    const explicitFlowProfile = stateFlowProfile || searchFlowProfile;

    if (state?.linkedSessionContext) {
      const withFlow =
        explicitFlowProfile && !state.linkedSessionContext.flowProfile
          ? { ...state.linkedSessionContext, flowProfile: explicitFlowProfile as LinkedSessionContext['flowProfile'] }
          : state.linkedSessionContext;
      setLinkedSessionContext(withFlow);
      const linkedRef = linkedSessionRefFromContext(withFlow);
      const linkedTitle = cleanLinkedSessionTitle(withFlow.sessionTitle);
      setExam((current) =>
        current.linkedSession && !linkedTitle
          ? current
          : {
              ...current,
              ...(current.linkedSession ? {} : { linkedSession: linkedRef, courseId: current.courseId ?? courseId }),
              ...(linkedTitle ? { title: linkedTitle } : {}),
            },
      );
    } else if (explicitFlowProfile) {
      setLinkedSessionContext((current) =>
        current
          ? current.flowProfile
            ? current
            : { ...current, flowProfile: explicitFlowProfile as LinkedSessionContext['flowProfile'] }
          : current,
      );
    }

    if (state?.teacherPrompt?.trim()) {
      setWizardTeacherPrompt(state.teacherPrompt.trim());
    }

    const intent = parseMaterialIntent(new URLSearchParams(location.search).get('intent'));
    const shouldAutoOpenAi =
      !aiAutoOpenedRef.current &&
      !examId &&
      (state?.openAiDialog || intent !== null);

    if (shouldAutoOpenAi) {
      aiAutoOpenedRef.current = true;
      setAiOpen(true);
      if (state?.openAiDialog) {
        const { openAiDialog: _omit, ...rest } = state;
        const nextState = Object.keys(rest).length > 0 ? rest : null;
        navigate(`${location.pathname}${location.search}`, { replace: true, state: nextState });
      }
      return;
    }

    if (!state?.generatedNotice && !state?.fromRecipeNativeBlock) return;

    const parts: string[] = [];
    if (state.fromRecipeNativeBlock) {
      parts.push('Bloque nativo generado — edítalo aquí o añade más bloques a la guía.');
    }
    if (state.generatedNotice) parts.push(state.generatedNotice);
    if (parts.length) setSavedMsg(parts.join(' '));

    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [examId, location.pathname, location.search, location.state, navigate]);

  const subjectExpertPrompt =
    SUBJECT_CATALOG.find((subject) => subject.tag === exam.subjectTag)?.aiExpertPrompt ?? undefined;
  const subjectLabel =
    SUBJECT_CATALOG.find((subject) => subject.tag === exam.subjectTag)?.name ?? exam.subjectTag;
  const subjectId =
    SUBJECT_CATALOG.find((subject) => subject.tag === exam.subjectTag)?.id ?? defaultSubjectTag;
  const liveLaunchUrl =
    exam.blocks.length > 0
      ? buildHostLaunchUrl({
          kind: 'exam',
          resourceId: exam.id,
          title: exam.title,
          subjectId,
        })
      : null;

  const addBlock = (mode: GameMode) => {
    const block: EvaluationBlock = {
      id: createBlockId(),
      index: exam.blocks.length,
      mode,
      label: EXAM_BLOCK_LABELS[mode] ?? GAME_MODE_CATALOG[mode].displayName,
      payload: defaultPayloadForMode(mode) as EvaluationBlock['payload'],
    };
    setExam((e) => ({ ...e, blocks: [...e.blocks, block] }));
  };

  const removeBlock = (id: string) => {
    setExam((e) => ({
      ...e,
      blocks: e.blocks.filter((b) => b.id !== id).map((b, i) => ({ ...b, index: i })),
    }));
  };

  const updateBlock = (blockId: string, next: EvaluationBlock) => {
    setExam((current) => ({
      ...current,
      blocks: current.blocks.map((block) => (block.id === blockId ? next : block)),
    }));
  };

  const navigateAfterGeneration = (nextExam: EvaluationExam, notice?: string | null) => {
    if (nextExam.blocks.length === 0) return;
    try {
      const levelFromOptions =
        courseProfile?.level ||
        linkedSessionContext?.level ||
        (nextExam as EvaluationExam).level;
      const withLevel: EvaluationExam = levelFromOptions
        ? { ...nextExam, level: String(levelFromOptions) }
        : nextExam;
      const saved = saveEvaluationExam(validateExam(examWithCourseContext(withLevel, courseId)));
      setExam(saved);

      // Guías / fichas imprimibles: el estudio print+chat es el editor canónico.
      if (isGuide || (!isInteractiveClass && saved.deliveryFormat !== 'interactive')) {
        navigate(buildPrintWorksheetUrl(saved.id, { edit: true }), {
          replace: true,
          state: {
            fromGeneration: true,
            ...(notice ? { generatedNotice: notice } : {}),
          },
        });
        return;
      }

      const defaultNotice = isInteractiveClass
        ? 'Clase generada — revisa las diapositivas y transmite desde la Consola o desde el curso.'
        : 'Evaluación generada — revisa los bloques antes de publicar o transmitir.';
      setSavedMsg(notice ?? defaultNotice);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo guardar el material generado');
    }
  };

  const handleAiBuild = async (options: ExamAiBuildOptions) => {
    setAiGenerating(true);
    setError(null);
    setSavedMsg(null);
    try {
      const {
        exam: generated,
        repertoireSearch,
        humanitiesResearch,
        knowledgeBaseUsed,
        provider,
        factoryFillCount,
        isDegraded,
        blocksGenerated,
        targetBlocks,
        guideDesign: nextGuideDesign,
      } = await generateExamWithAi({
        prompt: options.prompt,
        contentContext: options.contentContext,
        teacherId: exam.teacherId,
        subjectTag: exam.subjectTag,
        subjectExpertPrompt,
        extension: options.extension,
        level: options.level,
        focus: options.focus,
        enfoqueDidactico: options.enfoqueDidactico,
        mode: options.mode,
        config: options.config,
        resourceType: options.resourceType ?? (isGuide ? 'guide' : 'exam'),
        musicPrefs: options.musicPrefs,
        scoreContext: options.scoreContext ?? undefined,
        knowledgeBase: options.knowledgeBase ?? undefined,
        generationMode: options.generationMode,
        curriculumTarget: options.curriculumTarget,
        guideLength: options.guideLength,
        deliveryFormat: options.deliveryFormat ?? (isInteractiveClass ? 'interactive' : isGuide ? 'print' : undefined),
        deliveryProfile: options.deliveryProfile ?? deliveryProfile,
        courseProfile: courseProfile ?? undefined,
        linkedSessionContext: (() => {
          const base = linkedSessionContext ?? options.linkedSessionContext ?? undefined;
          if (!base && !options.visualAnchor) return undefined;
          if (!base) return undefined;
          return options.visualAnchor ? { ...base, visualAnchor: options.visualAnchor } : base;
        })(),
        visualAnchor: options.visualAnchor,
      });
      setGuideDesign(nextGuideDesign ?? null);
      const linkedRef =
        exam.linkedSession ??
        (linkedSessionContext
          ? linkedSessionRefFromContext(linkedSessionContext)
          : options.linkedSessionContext
            ? linkedSessionRefFromContext(options.linkedSessionContext)
            : undefined);
      const nextExam: EvaluationExam = {
        ...generated,
        id: exam.id,
        createdAt: exam.createdAt,
        subjectTag: exam.subjectTag || generated.subjectTag,
        resourceType: generated.resourceType ?? (isGuide ? 'guide' : 'exam'),
        deliveryProfile: generated.deliveryProfile ?? deliveryProfile,
        courseId: exam.courseId ?? courseId ?? generated.courseId,
        linkedSession: linkedRef,
        deliveryFormat: isInteractiveClass
          ? 'interactive'
          : isGuide
            ? 'print'
            : generated.deliveryFormat,
        settings: isInteractiveClass
          ? {
              ...generated.settings,
              advanceMode: 'HOST',
              integrityMode: generated.settings?.integrityMode ?? 'FLEXIBLE',
            }
          : generated.settings,
      };
      setExam(nextExam);

      let notice: string | null = null;
      if (isGuide && isDegraded && targetBlocks && blocksGenerated != null) {
        notice = `Generación parcial: ${blocksGenerated} de ${targetBlocks} bloques. Revisa el contenido y completa antes de publicar.`;
      } else if (isGuide && provider === 'topic_factory') {
        notice =
          'Mistral no entregó bloques válidos — se armó un borrador desde plantillas del tema. Revisa y regenera si hace falta.';
      } else if (isGuide && provider === 'partial_factory' && (factoryFillCount ?? 0) > 0) {
        notice = `${factoryFillCount} bloque(s) usaron plantillas del sistema porque Mistral no los entregó válidos. Edítalos o regenera la guía antes de publicar.`;
      } else if (isGuide && knowledgeBaseUsed) {
        notice = 'Guía basada en tu material docente — la IA no debe inventar datos fuera del archivo.';
      } else if (isGuide && humanitiesResearch) {
        notice =
          humanitiesResearch.confidence !== 'none'
            ? `Investigación previa (${humanitiesResearch.confidence}) · ${humanitiesResearch.sources.length} fuente(s) para rigor histórico/literario.`
            : 'Sin fuentes web claras — evita citas inventadas en source_analysis.';
      } else if (isGuide && repertoireSearch) {
        const mode =
          repertoireSearch.verificationMode === 'cultural'
            ? `Contexto web encontrado (${repertoireSearch.confidence}) · ${repertoireSearch.sources.length} fuente(s).`
            : 'Sin contexto web claro — la guía usará introducción técnica sin inventar folklore.';
        notice =
          provider === 'scaffold'
            ? `${mode} La IA no entregó bloques válidos — se armó la guía desde la partitura.`
            : mode;
      } else if (isGuide && provider === 'scaffold') {
        notice = 'La IA no entregó bloques válidos — se armó la guía desde la partitura.';
      } else if (isGuide && provider === 'mistral') {
        notice = 'Guía generada con IA — revisa el contenido antes de publicar.';
      }

      navigateAfterGeneration(nextExam, notice);
    } catch (e) {
      const raw = e instanceof Error ? e.message : 'generate_exam_failed';
      const code = raw.includes('::') ? raw.slice(0, raw.indexOf('::')) : raw;
      if (code === 'prompt_too_short') {
        setError(mapGenerateExamError(raw));
        return;
      }
      const isMusicWithScore =
        exam.subjectTag === 'musica' &&
        Boolean(
          (options.scoreContext as { summary?: { bars?: unknown } } | undefined)?.summary?.bars,
        );
      if (isGuide && isMusicWithScore) {
        setError(
          `No se pudo generar la guía con IA (${mapGenerateExamError(raw)}). Verifica la partitura e intenta de nuevo.`,
        );
        return;
      }
      const allowClientFallback =
        code === 'mistral_not_configured' ||
        code === 'mistral_auth_failed' ||
        code === 'Failed to fetch';
      if (isGuide && !allowClientFallback) {
        setError(mapGenerateExamError(raw) || `No se pudo generar la guía (${code}).`);
        return;
      }
      try {
        const ext = getExtensionOption(options.extension);
        const generated = generateExamFromOptions({
          prompt: options.prompt,
          teacherId: exam.teacherId,
          blockCount: ext.exactBlocks,
          focus: options.focus,
          subjectTag: exam.subjectTag,
          resourceType: isGuide ? 'guide' : 'exam',
        });
        const nextExam: EvaluationExam = {
          ...generated,
          id: exam.id,
          createdAt: exam.createdAt,
          subjectTag: exam.subjectTag,
          resourceType: isGuide ? 'guide' : isInteractiveClass ? 'exam' : generated.resourceType,
          deliveryFormat: isInteractiveClass
            ? 'interactive'
            : isGuide
              ? 'print'
              : undefined,
          settings: isInteractiveClass
            ? {
                ...generated.settings,
                advanceMode: 'HOST',
                integrityMode: generated.settings?.integrityMode ?? 'FLEXIBLE',
              }
            : generated.settings,
        };
        setExam(nextExam);
        const notice =
          code === 'mistral_not_configured' || code === 'mistral_auth_failed'
            ? mapGenerateExamError(code) + (isGuide ? ' Se armó un borrador local.' : ' Se usó el generador local.')
            : `No se pudo generar con IA (${mapGenerateExamError(code)}) — se usó el generador local.`;
        setError(notice);
        navigateAfterGeneration(nextExam, notice);
      } catch (fallbackError) {
        setError(fallbackError instanceof Error ? fallbackError.message : mapGenerateExamError(code));
      }
    } finally {
      setAiGenerating(false);
    }
  };

  const save = () => {
    try {
      const validated = validateExam(examWithCourseContext(exam, courseId));
      const saved = saveEvaluationExam(validated);
      setExam(saved);
      setError(null);
      setSavedMsg('Borrador guardado en este dispositivo. Publica para activar el código de acceso.');
      onSaved?.(saved);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al guardar');
    }
  };

  const programAsync = async () => {
    setProgramming(true);
    setProgramError(null);
    setProgramMsg(null);
    try {
      const validated = programExamAccessCode(validateExam(examWithCourseContext(exam, courseId)));
      const saved = saveEvaluationExam(validated);
      setExam(saved);

      const teacherId =
        saved.teacherId ||
        (typeof localStorage !== 'undefined' ? localStorage.getItem('sala_host_id') : null) ||
        'teacher';

      const result = await programScheduledExam(saved, teacherId);
      const updated: EvaluationExam = {
        ...saved,
        accessCode: result.accessCode,
        asyncRoomId: result.roomId,
        asyncRoomJoinCode: result.joinCode,
        programmedAt: result.programmedAt,
      };
      saveEvaluationExam(updated);
      setExam(updated);
      setProgramMsg(
        `Publicada · Los alumnos entran con el código ${result.accessCode} (no es el código de sala en vivo)`,
      );
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Error al publicar';
      setProgramError(mapExamJoinError(msg));
    } finally {
      setProgramming(false);
    }
  };

  const openPrintWorksheet = () => {
    try {
      const validated = validateExam(examWithCourseContext(exam, courseId));
      const saved = saveEvaluationExam(validated);
      setExam(saved);
      setError(null);
      window.open(buildPrintWorksheetUrl(saved.id), '_blank', 'noopener,noreferrer');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Guarda la evaluación antes de imprimir');
    }
  };

  const openMonitor = () => {
    if (!exam.asyncRoomId || !exam.accessCode) return;
    const teacherId =
      exam.teacherId ||
      (typeof localStorage !== 'undefined' ? localStorage.getItem('sala_host_id') : null) ||
      'teacher';
    try {
      localStorage.setItem('sala_host_id', teacherId);
    } catch {
      /* ignore */
    }
    saveHostSession({
      roomId: exam.asyncRoomId,
      joinCode: exam.asyncRoomJoinCode || exam.accessCode,
      hostId: teacherId,
    });
    window.location.href = '/host';
  };

  const guideDesignBadge = isGuide ? resolveGuideDesignBadge(guideDesign) : null;

  return (
    <div className="exam-builder flex min-h-0 flex-1 flex-col gap-4">
      <div className="flex flex-wrap items-end gap-3">
        <label className="min-w-[200px] flex-1">
          <span className="sala-label">
            {isGuide ? 'Título de la guía' : isInteractiveClass ? 'Título de la clase' : 'Título de la evaluación'}
          </span>
          <input
            className="sala-input-dark mt-1 w-full"
            value={exam.title}
            onChange={(e) => setExam((ex) => ({ ...ex, title: e.target.value }))}
          />
        </label>
        {guideDesignBadge ? (
          <span
            className={`guide-design-badge guide-design-badge--${guideDesignBadge.tone}`}
            title={guideDesignBadge.tooltip}
            role="status"
          >
            {guideDesignBadge.label}
            {Array.isArray(guideDesign?.mutations) && guideDesign.mutations.length > 0
              ? ` · ${guideDesign.mutations.length} post-LLM`
              : ''}
          </span>
        ) : null}
        <label className="w-40">
          <span className="sala-label">Asignatura</span>
          <input
            className="sala-input-dark mt-1 w-full text-sm"
            value={exam.subjectTag}
            onChange={(e) => setExam((ex) => ({ ...ex, subjectTag: e.target.value }))}
          />
        </label>
        <button type="button" className="sala-btn-outline text-xs" onClick={() => setAiOpen(true)}>
          ✨{' '}
          {isGuide
            ? 'Generar guía con IA'
            : isInteractiveClass
              ? 'Generar clase con IA'
              : 'Generar con IA'}
        </button>
      </div>

      {!isGuide && !isInteractiveClass && (
      <details className="rounded-lg border border-[var(--sala-border)] bg-[var(--sala-surface-raised)] px-3 py-2 text-xs">
        <summary className="cursor-pointer text-[var(--sala-ink-dim)]">
          Opciones de sala digital (avanzado)
        </summary>
        <div className="mt-2 flex flex-wrap gap-3">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={exam.settings.integrityMode === 'STRICT'}
            onChange={(e) =>
              setExam((ex) => ({
                ...ex,
                settings: { ...ex.settings, integrityMode: e.target.checked ? 'STRICT' : 'FLEXIBLE' },
              }))
            }
          />
          Modo integridad estricto (anti-foco)
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={exam.settings.advanceMode === 'AUTONOMOUS'}
            onChange={(e) =>
              setExam((ex) => ({
                ...ex,
                settings: {
                  ...ex.settings,
                  advanceMode: e.target.checked ? 'AUTONOMOUS' : 'HOST',
                },
              }))
            }
          />
          Avance autónomo (cada alumno a su ritmo)
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={exam.settings.shuffleBlocks}
            onChange={(e) =>
              setExam((ex) => ({ ...ex, settings: { ...ex.settings, shuffleBlocks: e.target.checked } }))
            }
          />
          Mezclar bloques e ítems (orden único por alumno en modo autónomo)
        </label>
        </div>
        <p className="mt-2 text-[10px] text-[var(--sala-ink-dim)]">
          Estas opciones aplican al lanzar en sala en vivo. Preferí iniciar desde la vista del curso.
        </p>
      </details>
      )}

      {isGuide && (
        <p className="text-xs text-[var(--sala-ink-dim)]">
          Ficha imprimible · COPISI · enforcers de impresión · ideal para fotocopiadora o cuaderno.
        </p>
      )}

      {isInteractiveClass && (
        <p className="text-xs text-[var(--sala-acid-mint-ink)]">
          Clase proyectable · avance controlado por docente (HOST) · bloques visuales a pantalla completa.
        </p>
      )}

      {error && <p className="text-sm text-red-400">{error}</p>}
      {savedMsg && <p className="text-sm text-emerald-400">{savedMsg}</p>}
      {programMsg && <p className="text-sm text-emerald-400">{programMsg}</p>}
      {programError && <p className="text-sm text-red-400">{programError}</p>}

      {exam.programmedAt && exam.accessCode && !isGuide && (
        <div className="rounded-xl border border-[var(--sala-border)] bg-[var(--sala-surface-raised)] px-4 py-3">
          <p className="sala-label">Código de evaluación (async) · publicada</p>
          <p className="font-display text-3xl font-extrabold tracking-[0.2em] text-[var(--sala-accent-neon)]">
            {exam.accessCode}
          </p>
          <p className="mt-1 text-xs text-[var(--sala-ink-dim)]">
            Comparte este código para que los alumnos entren en{' '}
            <Link to={`/join?mode=exam&code=${exam.accessCode}`} className="text-[var(--sala-accent)] underline">
              /join → Evaluación async
            </Link>
            . Es distinto del código de sala en vivo.
          </p>
          {exam.programmedAt && (
            <p className="mt-2 text-[10px] text-[var(--sala-ink-dim)]">
              Publicada en servidor · {new Date(exam.programmedAt).toLocaleString()}
            </p>
          )}
        </div>
      )}

      {!isGuide && !exam.programmedAt && exam.blocks.length > 0 && (
        <p className="text-xs text-[var(--sala-ink-dim)]">
          El código para alumnos se genera al publicar con «Publicar evaluación async».
        </p>
      )}

      {exam.blocks.length > 0 && (
        <div className="exam-delivery-card">
          <p className="exam-delivery-card__kicker">
            {isInteractiveClass
              ? '¿Listo para proyectar en la Consola?'
              : '¿Cómo vas a aplicar esta actividad hoy?'}
          </p>
          <div className="exam-delivery-card__options">
            {liveLaunchUrl ? (
              <Link
                to={liveLaunchUrl}
                className={`exam-delivery-option exam-delivery-option--digital${isInteractiveClass ? ' exam-delivery-option--interactive' : ''}`}
              >
                <span className="exam-delivery-option__icon">{isInteractiveClass ? '📽️' : '📱'}</span>
                <span className="exam-delivery-option__body">
                  <strong>{isInteractiveClass ? 'Transmitir clase en vivo' : 'Lanzar sala digital'}</strong>
                  <span>
                    {isInteractiveClass
                      ? 'Proyector · HOST · avance bloque a bloque'
                      : 'Celulares · tiempo real · podio'}
                  </span>
                </span>
              </Link>
            ) : (
              <button type="button" className="exam-delivery-option exam-delivery-option--digital" disabled>
                <span className="exam-delivery-option__icon">{isInteractiveClass ? '📽️' : '📱'}</span>
                <span className="exam-delivery-option__body">
                  <strong>{isInteractiveClass ? 'Transmitir clase en vivo' : 'Lanzar sala digital'}</strong>
                  <span>Guarda bloques primero</span>
                </span>
              </button>
            )}
            {!isInteractiveClass && (
            <button
              type="button"
              className="exam-delivery-option exam-delivery-option--print"
              onClick={openPrintWorksheet}
            >
              <span className="exam-delivery-option__icon">🖨️</span>
              <span className="exam-delivery-option__body">
                <strong>Exportar guía de trabajo</strong>
                <span>PDF · imprimir · aula sin Wi-Fi</span>
              </span>
            </button>
            )}
          </div>
        </div>
      )}

      <div
        className={
          isGuide
            ? 'exam-builder__timeline exam-builder__timeline--guide min-h-0 flex-1 overflow-y-auto p-1'
            : 'exam-builder__timeline min-h-0 flex-1 overflow-y-auto rounded-xl border border-[var(--sala-border)] p-4'
        }
      >
        <p className="sala-label mb-3">
          {isGuide
            ? `Contenido de la guía · ${exam.blocks.length} bloques`
            : `Línea de tiempo · ${exam.blocks.length} bloques`}
        </p>
        {exam.blocks.length === 0 ? (
          <div className="sala-empty-state py-8">
            <p className="sala-empty-state__title">{isGuide ? 'Guía vacía' : 'Mazo vacío'}</p>
            <p className="sala-empty-state__hint">
              Usa «
              {isGuide ? 'Generar guía con IA' : isInteractiveClass ? 'Generar clase con IA' : 'Generar con IA'}» o
              añade bloques manualmente.
            </p>
          </div>
        ) : (
          <ol className={isGuide ? 'guide-block-flow' : 'space-y-2'}>
            {exam.blocks.map((block, i) => (
              <li key={block.id}>
                <ExamBlockAccordion
                  block={block}
                  index={i}
                  open={expandedBlockId === block.id}
                  onOpenChange={(open) => setExpandedBlockId(open ? block.id : null)}
                  onChange={(next) => updateBlock(block.id, next)}
                  onRemove={() => {
                    if (expandedBlockId === block.id) setExpandedBlockId(null);
                    removeBlock(block.id);
                  }}
                  variant={isGuide ? 'guide' : 'default'}
                  postLlmMutations={
                    isGuide && Array.isArray(guideDesign?.mutations)
                      ? guideDesign.mutations.filter(
                          (m) => m.blockId === block.id || m.blockId === `idx:${i}`,
                        )
                      : []
                  }
                  examContext={{
                    examTitle: exam.title,
                    subjectTag: exam.subjectTag,
                    resourceType: exam.resourceType ?? defaultResourceType,
                    blockIndex: i,
                    guideContext: isGuide ? buildGuideContextFromBlocks(exam.blocks, i) : undefined,
                    subjectExpertPrompt,
                    // Sin inventar 6basico: curso → exam.level persistido.
                    level: courseProfile?.level || exam.level,
                  }}
                />
              </li>
            ))}
          </ol>
        )}
      </div>

      <div className="exam-builder__picker shrink-0">
        <p className="sala-label mb-2">{isGuide ? '+ Agregar a la guía' : '+ Añadir bloque'}</p>
        <div className="flex flex-wrap gap-2">
          {blockModes.map((mode) => (
            <button
              key={mode}
              type="button"
              className="sala-btn-outline py-1.5 text-[10px]"
              onClick={() => addBlock(mode)}
            >
              {isGuide ? null : <>{GAME_MODE_CATALOG[mode].icon} </>}
              {EXAM_BLOCK_LABELS[mode] ?? GAME_MODE_CATALOG[mode].displayName}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap justify-end gap-2">
        <button type="button" className="sala-btn-outline text-xs" onClick={save}>
          Guardar borrador
        </button>
        {liveLaunchUrl && (
          <Link to={liveLaunchUrl} className="sala-btn-hero text-xs">
            {isInteractiveClass ? 'Transmitir en vivo' : 'Usar en vivo'}
          </Link>
        )}
        {!isGuide && !isInteractiveClass && (
        <button
          type="button"
          className="sala-btn-outline text-xs"
          disabled={programming || exam.blocks.length === 0}
          onClick={() => void programAsync()}
        >
          {programming ? 'Publicando…' : 'Publicar evaluación async'}
        </button>
        )}
        {!isGuide && exam.asyncRoomId && exam.asyncRoomJoinCode && (
          <button type="button" className="sala-btn-outline text-xs" onClick={openMonitor}>
            Abrir monitor docente
          </button>
        )}
      </div>

      <ExamAiBuildDialog
        open={aiOpen}
        onOpenChange={setAiOpen}
        subjectLabel={subjectLabel}
        subjectId={subjectId}
        subjectTag={exam.subjectTag}
        resourceType={isGuide ? 'guide' : 'exam'}
        materialIntent={resolvedIntent}
        linkedSessionContext={linkedSessionContext ?? undefined}
        teacherPromptSeed={wizardTeacherPrompt}
        courseProfile={courseProfile}
        loading={aiGenerating}
        onConfirm={(options) => void handleAiBuild(options)}
      />

      <GenerationLoadingOverlay visible={aiGenerating} />
    </div>
  );
}
